import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { getUserCredits, consumeCredits } from '../../../services/creditService';
import { useDetailedEstimate } from '../../../hooks/estimation';
import { DETAILED_STEPS } from './config';
import { StepperNav } from './navigation';
import LoadingSpinner from '../../ui/LoadingSpinner';
import ConfirmDialog from '../../ui/ConfirmDialog';
import { CreditCard } from 'lucide-react';
import DetailedEstimationStepper from './navigation/DetailedEstimationStepper';

export const DetailedEstimate: React.FC<{ initialData?: any }> = ({ initialData }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [credits, setCredits] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [showFirstPageConfirm, setShowFirstPageConfirm] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isPaid, setIsPaid] = useState(false);

  const {
    currentStep,
    completedSteps,
    formData,
    handleStepClick,
    handleNext: baseHandleNext,
    handleBack: baseHandleBack,
    handleStepDataChange,
    calculateEstimate
  } = useDetailedEstimate(initialData);

  // Fonction pour générer une clé unique pour l'estimation
  const getEstimateKey = () => {
    if (initialData) {
      return `estimate_paid_${initialData.id}`;
    }
    // Pour une nouvelle estimation, utiliser l'adresse comme identifiant unique
    if (formData.description?.location?.address) {
      return `estimate_paid_${btoa(formData.description.location.address)}`;
    }
    return null;
  };

  useEffect(() => {
    const checkCredits = async () => {
      if (!user) {
        navigate('/login', { 
          state: { 
            from: location,
            message: "Pour accéder à l'estimation détaillée, veuillez vous connecter ou créer un compte"
          }
        });
        return;
      }

      try {
        // Vérifier si l'estimation est déjà payée
        const estimateKey = getEstimateKey();
        if (estimateKey) {
          const savedIsPaid = localStorage.getItem(estimateKey);
          if (savedIsPaid === 'true') {
            setIsPaid(true);
            setCredits(1); // Simulation d'un crédit disponible
            setLoading(false);
            return;
          }
        }

        // Pour une édition, considérer comme déjà payé
        if (initialData) {
          setIsPaid(true);
          setCredits(1);
        } else {
          // Pour une nouvelle estimation
          const availableCredits = await getUserCredits(user.uid);
          setCredits(availableCredits);
          
          // Rediriger uniquement si pas de crédits et pas encore payé
          if (availableCredits === 0 && !isPaid) {
            navigate('/credits/purchase', {
              state: {
                message: "Un crédit est nécessaire pour réaliser une estimation détaillée"
              }
            });
            return;
          }
        }
      } catch (err) {
        console.error('Erreur lors de la vérification des crédits:', err);
        setError("Impossible de vérifier vos crédits");
      } finally {
        setLoading(false);
      }
    };
    
    checkCredits();
  }, [user, navigate, location, initialData, formData.description?.location?.address]);

  const handleStepNavigation = (nextStep: string) => {
    // Empêcher le retour à la première page si elle est déjà complétée
    if (currentStep === 'description' && !completedSteps.includes('description') && !isPaid) {
      setPendingNavigation(nextStep);
      setShowFirstPageConfirm(true);
      return;
    }

    handleStepClick(nextStep);
  };

  const handleNext = () => {
    const nextStep = DETAILED_STEPS[DETAILED_STEPS.findIndex(s => s.id === currentStep) + 1].id;
    handleStepNavigation(nextStep);
  };

  const handleBack = () => {
    // Empêcher le retour à l'étape description si elle est complétée
    if (currentStep === 'preparation' && completedSteps.includes('description')) {
      return;
    }
    
    // Find the current step index
    const currentIndex = DETAILED_STEPS.findIndex(s => s.id === currentStep);
    
    // Only proceed if we're not at the first step
    if (currentIndex > 0) {
      const prevStep = DETAILED_STEPS[currentIndex - 1].id;
      handleStepNavigation(prevStep);
    }
  };
  
  const confirmFirstPage = async () => {
    if (!user || !formData.description?.location?.address) {
      setError("Informations incomplètes");
      return;
    }

    try {
      // Consommer un crédit
      await consumeCredits(user.uid, 1, {
        operationType: 'detailed_estimate',
        address: formData.description.location.address
      });

      // Marquer l'estimation comme payée avec une clé unique
      const estimateKey = getEstimateKey();
      if (estimateKey) {
        localStorage.setItem(estimateKey, 'true');
        setIsPaid(true);
      }

      // Mettre à jour le nombre de crédits
      const newCredits = await getUserCredits(user.uid);
      setCredits(newCredits);

      // Fermer la confirmation et continuer la navigation
      setShowFirstPageConfirm(false);
      if (pendingNavigation) {
        handleStepClick(pendingNavigation);
        setPendingNavigation(null);
      } else {
        handleNext();
      }
    } catch (err) {
      console.error('Erreur lors de la confirmation:', err);
      if (err instanceof Error && err.message === 'Solde de crédits insuffisant') {
        navigate('/credits/purchase', {
          state: { message: "Vous n'avez plus de crédits disponibles" }
        });
      } else {
        setError("Une erreur est survenue lors de la validation");
      }
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-neutral-50 pt-24">
      {/* Affichage des crédits disponibles */}
      <div className="fixed top-16 right-4 bg-white rounded-lg shadow-lg p-4 z-50">
        <div className="flex items-center space-x-2">
          <CreditCard className="h-5 w-5 text-primary" />
          <span className="font-medium">
            {isPaid ? "Estimation payée" : `Crédits disponibles : ${credits}`}
          </span>
        </div>
        {currentStep === 'description' && !completedSteps.includes('description') && !isPaid && (
          <p className="text-sm text-neutral-500 mt-2">
            Un crédit sera débité pour chaque nouvelle adresse estimée
          </p>
        )}
      </div>

      <DetailedEstimationStepper
        currentStep={currentStep}
        completedSteps={completedSteps}
        onStepClick={handleStepNavigation}
      />

      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="bg-white rounded-lg shadow-lg p-6">
          {error && (
            <div className="mb-6 p-4 bg-red-50 text-red-600 rounded-lg">
              {error}
            </div>
          )}
          {React.createElement(DETAILED_STEPS.find(s => s.id === currentStep)!.component, {
            data: currentStep === 'resultat' ? formData : formData[currentStep],
            onChange: (data: any) => handleStepDataChange(currentStep, data),
            onNext: handleNext,
            onBack: handleBack,
            estimate: currentStep === 'resultat' ? calculateEstimate() : undefined
          })}
        </div>
      </div>

      {/* Dialog de confirmation première page */}
      <ConfirmDialog
        isOpen={showFirstPageConfirm}
        title="Votre crédit a été utilisé"
        message="Votre bien immobilier sera disponible depuis votre dashboard. Vous pourrez désormais faire autant d'estimations détaillées que vous le souhaitez sur ce bien."
        confirmLabel="Confirmer"
        onConfirm={confirmFirstPage}
        onCancel={() => {
          setShowFirstPageConfirm(false);
          setPendingNavigation(null);
        }}
        variant="warning"
      />
    </div>
  );
};

export default DetailedEstimate;